import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { Button } from "./Button";

const ConfirmContext = createContext(null);

/*
 * Replaces window.confirm for the destructive paths (go LIVE, square off,
 * activate a rule, delete). Same semantics — one deliberate confirmation —
 * but themed, readable, keyboard-driven, and it never steals focus from the
 * page the way a native modal dialog does.
 */
export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    let opts = options || {};
    if (typeof options === "string") {
      // Plain-string callers pass "Headline?" then a blank line then detail.
      const [head, ...rest] = options.split("\n\n");
      opts = head.length <= 90 && rest.length
        ? { title: head, message: rest.join("\n\n") }
        : { message: options };
    }
    setRequest({
      title: opts.title || "Are you sure?",
      message: opts.message || "",
      confirmLabel: opts.confirmLabel || "Confirm",
      cancelLabel: opts.cancelLabel || "Cancel",
      danger: opts.danger !== false,
    });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value) => {
    setRequest(null);
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") settle(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [request, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={request.title}>
          <button type="button" tabIndex={-1} aria-hidden onClick={() => settle(false)} className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm animate-fade-in" />

          <div className={`relative w-full max-w-md overflow-hidden rounded-2xl border bg-surface shadow-pop animate-in zoom-in-95 ${request.danger ? "border-rose-500/35" : "border-line-strong"}`}>
            <div className="flex gap-3.5 p-5">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${
                request.danger
                  ? "border-rose-500/30 bg-rose-500/[.1] text-rose-400"
                  : "border-cyan-500/30 bg-cyan-500/[.1] text-cyan-400"
              }`}>
                {request.danger ? <ShieldAlert className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-hi">{request.title}</h2>
                {request.message && (
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-low">{request.message}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-line bg-surface-2 px-5 py-3.5">
              <Button variant="ghost" size="sm" onClick={() => settle(false)}>{request.cancelLabel}</Button>
              <Button
                autoFocus
                variant={request.danger ? "danger" : "primary"}
                size="sm"
                onClick={() => settle(true)}
              >
                {request.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

/** `const ok = await confirm("…")` — resolves false on cancel/Esc. */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return ctx;
}
